import { gql, useApolloClient } from "@apollo/client";
import Archive from "../components/Archive";
import PageBannerBlog from "../components/PageBannerBlog";
import SiteWrapper from "../components/SiteWrapper";
import * as MENUS from "../constants/menus";
import {
  BlogInfoFragment,
  FeatureImageFragment,
  NavigationMenuFragment,
  SiteSettingsFragment,
} from "../fragments/GeneralSettings";
import { SingleFormFragment } from "../fragments/GravityForms";
import { BlogPagination } from "../components/BlogPagination";
import { useState } from "react";

export default function Component(props) {
  const client = useApolloClient();

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 12;

  // This is now safe because total exists
  const totalPosts = props.data.posts.pageInfo.total;
  const totalPages = Math.ceil(totalPosts / postsPerPage);

  const [postsProps, setPostsProps] = useState(() => {
    let base = { ...props.data.posts };
    if (props.data.page?.seo) base.seo = props.data.page.seo;
    base.categories = props.data.categories;
    base.title = "Resources";
    return base;
  });

  const siteProps = {
    ...props.data,
    posts: postsProps,
    page: props.data.page,
    postType: "posts",
  };

  const initialEndCursor = props.data.posts?.pageInfo?.endCursor ?? null;

  const [pageCursors, setPageCursors] = useState({
    1: null, // Page 1 starts at null
    2: initialEndCursor, // Page 2 starts after Page 1's endCursor
  });

  const loadPage = async (page) => {
    const after = pageCursors[page] ?? null;
    const { data: newData } = await client.query({
      query: Component.query,
      variables: {
        headerLocation: MENUS.PRIMARY_LOCATION,
        formId: "1",
        first: postsPerPage,
        after,
      },
      fetchPolicy: "network-only",
    });

    if (newData?.posts) {
      setPostsProps({
        ...newData.posts,
        categories: props.data.categories,
        seo: props.data.page?.seo,
        title: "Resources",
      });

      // Store the cursor for next page
      setPageCursors((prev) => ({
        ...prev,
        [page + 1]: newData.posts.pageInfo.endCursor,
      }));

      setCurrentPage(page);
    }
  };

  if (props.loading) {
    return <>Loading...</>;
  }

  return (
    <SiteWrapper siteData={siteProps}>
      <PageBannerBlog />
      <Archive />
      <BlogPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={loadPage}
      />
    </SiteWrapper>
  );
}

Component.variables = ({}, ctx) => {
  return {
    headerLocation: MENUS.PRIMARY_LOCATION,
    asPreview: ctx?.asPreview,
  };
};

Component.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenuFragment}
  ${SingleFormFragment}
  ${SiteSettingsFragment}
  query GetArchivePage(
    $headerLocation: MenuLocationEnum
    $formId: ID = "1"
    $first: Int = 12
    $after: String
  ) {
    page(idType: DATABASE_ID, id: "3766") {
      title
      seo {
        canonical
        title
        metaDesc
        focuskw
        metaRobotsNoindex
        schema {
          pageType
          articleType
        }
        breadcrumbs {
          url
          text
        }
        opengraphUrl
      }
      featuredImage {
        node {
          altText
          mediaDetails {
            sizes {
              height
              width
              sourceUrl
            }
          }
        }
      }
    }
    categories(where: { exclude: [1] }) {
      nodes {
        id
        slug
        taxonomyName
        name
      }
    }
    posts(first: $first, after: $after) {
      pageInfo {
        total
        endCursor
        hasNextPage
        startCursor
        hasPreviousPage
      }
      nodes {
        date
        excerpt
        id
        link
        title
        featuredImage {
          node {
            id
            sourceUrl
            altText
            mediaDetails {
              width
              height
            }
            sizes(size: MEDIUM)
          }
        }
        categories {
          nodes {
            categoryId
            name
          }
        }
        tags {
          nodes {
            name
            link
            id
          }
        }
      }
    }

    themeStylesheet {
      value
    }
    generalSettings {
      ...BlogInfoFragment
    }
    footer1: menuItems(where: { location: FOOTER_COLUMN_1 }, last: 10000) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footer2: menuItems(where: { location: FOOTER_COLUMN_2 }, last: 10000) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footer3: menuItems(where: { location: FOOTER_COLUMN_3 }, last: 10000) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footer4: menuItems(where: { location: FOOTER_COLUMN_4 }, last: 10000) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }

    headerMenuItems: menuItems(
      where: { location: $headerLocation }
      last: 10000
    ) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }

    themeGeneralSettings {
      ...SiteSettingsFragment
    }

    newsletterSignup: gfForm(id: $formId, idType: DATABASE_ID) {
      ...SingleFormFields
    }
  }
`;
